import useToggle from "@/src/hooks/useToggle";
import { Stack } from "@mui/material";
import { ComponentType, useState } from "react";
import { HintArea } from "../../HintArea";
import { InjectedFieldHintProps } from "../lib/type";
import { useFieldDetailInfoModal } from "@/src/stores/useFieldDetailInfoModal";
import { useHintCardStore } from "@/src/stores/useHintCardStore";
import { useShallow } from "zustand/react/shallow";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { useFormContext } from "react-hook-form";

// "hints" prop will take priority on desktop while "hint" ll do the same on mobile
// UPD: "hints" now is only source of truth for hint card data. "hint" is only used for storing display condition (for now)
// TODO: remove "hints" as a separate prop and use "hintsList" from "hint" instead of it

export function withFieldHint<T extends InjectedFieldHintProps>(
  Component: ComponentType<T>,
) {
  const WithFieldHint = ({
    hint,
    hints,
    mobileHint = true,
    desktopHint = true,
    hintLabel,
    hintWrapperClassname,
    ...rest
  }: T) => {
    const {
      isToggled: isHintAreaVisible,
      toggle: toggleHintArea,
      open: openHintArea,
      close: closeHintArea,
    } = useToggle({
      initialState: false,
    });

    const [selectedHints, setSelectedHints] = useState<any>(null);

    const { getValues } = useFormContext();

    const typedRest = rest as any;

    const openHintDetailsModal = useFieldDetailInfoModal(
      useShallow((state) => state.open),
    );

    const {
      contentDescription,

      hintCardDetailsButton,
      hintCardDetailsButtonText,

      hintDetailsActions,
      hintDetailsDescription,
      hintDetailsTitle,
    } = hints?.[0] || {};

    const onDetailsButtonPressed = () => {
      openHintDetailsModal({
        title: hintDetailsTitle || "",
        actions: hintDetailsActions,
        description: hintDetailsDescription || "",
      });
    };
    const { setHintCardVariant, setHintsList } = useHintCardStore(
      useShallow(({ setCardVariant, setHintsList }) => ({
        setHintsList,
        setHintCardVariant: setCardVariant,
      })),
    );

    const isOptionsHintEnabled =
      typedRest?.options?.length &&
      typedRest?.options?.some(({ hint }: any) => !!hint);

    const onComponentFocus = () => {
      if (!desktopHint || !hint?.isShown) {
        return;
      }
      setHintCardVariant("focused");

      optionSelectedBlock: {
        if (!isOptionsHintEnabled) {
          break optionSelectedBlock;
        }
        const currentSelectedValue = getValues(typedRest?.name)?.value;
        if (!currentSelectedValue) {
          break optionSelectedBlock;
        }
        const selectedOption = typedRest.options.find(
          ({ value }: any) => value === currentSelectedValue,
        );

        const selectedOptionHints = selectedOption?.hint?.hintsList;
        const selectedOptionHintsLabel =
          selectedOption?.hint?.hintLabel || hintLabel;

        if (!selectedOptionHints?.length) {
          break optionSelectedBlock;
        }
        const selectedHints = hintLabel
          ? selectedOptionHints?.map((hint: any) => ({
              ...hint,
              contentTitle: selectedOptionHintsLabel,
            }))
          : selectedOptionHints;
        setSelectedHints(selectedHints);
        setHintsList(selectedHints);
        return;
      }
      if (hints) {
        const selectedHints = hintLabel
          ? hints.map((hint) => ({
              ...hint,
              contentTitle: hintLabel,
            }))
          : hints;
        setSelectedHints(selectedHints);
        setHintsList(selectedHints);
      }
    };

    const onComponentBlur = () => {
      if (!desktopHint) {
        return;
      }
      setHintCardVariant("default");
      // resetCurrentHint();
    };

    const onOptionSelect = (...args: any) => {
      const [e, value, reason, details] = args;
      typedRest.onChange?.(e, value, reason, details);

      const selectedOptionHints = value?.hint?.hintsList;
      const selectedOptionHintsLabel = value?.hint?.hintLabel || hintLabel;

      if (selectedOptionHints?.length) {
        const selectedHints = hintLabel
          ? selectedOptionHints?.map((hint: any) => ({
              ...hint,
              contentTitle: selectedOptionHintsLabel,
            }))
          : selectedOptionHints;
        setSelectedHints(selectedHints);
        setHintsList(selectedHints);
        // openHintArea();
        e.stopPropagation();
      }
    };

    const isItemsHintEnabled =
      typedRest?.items?.length &&
      typedRest?.items?.some(({ hint }: any) => !!hint);

    const onItemChange = (e: any, value: string) => {
      typedRest.onChange?.(value);

      const selectedItem = typedRest?.items?.find(
        ({ value: itemValue }: any) => itemValue === value,
      );

      if (!selectedItem) {
        return;
      }

      const selectedItemHint = selectedItem.hint;

      if (!selectedItemHint) {
        return;
      }

      const selectedItemHints = selectedItemHint.hintsList;
      const selectedItemHintsLabel = selectedItemHint?.hintLabel || hintLabel;

      if (selectedItemHints?.length) {
        const selectedHints = hintLabel
          ? selectedItemHints.map((hint: any) => ({
              ...hint,
              contentTitle: selectedItemHintsLabel,
            }))
          : selectedItemHints;
        setSelectedHints(selectedHints);
        setHintsList(selectedHints);
        setHintCardVariant("focused");
        e.stopPropagation();
      }
    };

    const onChange = isOptionsHintEnabled
      ? onOptionSelect
      : isItemsHintEnabled
        ? onItemChange
        : typedRest.onChange;

    const getSelectedHints = () => {
      if (!isOptionsHintEnabled) {
        // break optionSelectedBlock;
        return null;
      }
      const currentSelectedValue = getValues(typedRest?.name)?.value;
      if (!currentSelectedValue) {
        // break optionSelectedBlock;
        return null;
      }
      const selectedOption = typedRest.options.find(
        ({ value }: any) => value === currentSelectedValue,
      );

      const selectedOptionHints = selectedOption?.hint?.hintsList;
      const selectedOptionHintsLabel =
        selectedOption?.hint?.hintLabel || hintLabel;

      if (!selectedOptionHints?.length) {
        // break optionSelectedBlock;
        return null;
      }
      const selectedHints = hintLabel
        ? selectedOptionHints?.map((hint: any) => ({
            ...hint,
            contentTitle: selectedOptionHintsLabel,
          }))
        : selectedOptionHints;

      return selectedHints;
    };

    const selectedHint = getSelectedHints()?.[0];

    const hintDescription =
      hint?.mobileDescription ||
      selectedHints?.[0]?.contentDescription ||
      selectedHint?.contentDescription ||
      contentDescription;

    const canHintBeShown =
      isHintAreaVisible &&
      (!!contentDescription ||
        !!selectedHints?.[0]?.contentDescription ||
        !!selectedHint?.contentDescription);

    const hintAreaDetailsButtonText =
      selectedHints?.[0]?.hintCardDetailsButtonText ||
      selectedHint?.hintCardDetailsButtonText ||
      hintCardDetailsButtonText;

    const hintAreaDetailsButton =
      selectedHints?.[0]?.hintCardDetailsButton ||
      selectedHint?.hintCardDetailsButton ||
      hintCardDetailsButton;

    const onHintAreaDetailsButtonPressed = () => {
      const selectedHint = getSelectedHints()?.[0];
      openHintDetailsModal({
        title:
          selectedHints?.[0]?.hintDetailsTitle ||
          selectedHint?.hintDetailsTitle ||
          hintDetailsTitle ||
          "",
        actions:
          selectedHints?.[0]?.hintDetailsActions ||
          selectedHint?.hintDetailsActions ||
          hintDetailsActions,
        description:
          selectedHints?.[0]?.hintDetailsDescription ||
          selectedHint?.hintDetailsDescription ||
          hintDetailsDescription ||
          "",
      });
    };

    return (
      <Stack
        className={cn(
          "flex w-full cursor-pointer flex-col gap-2",
          hintWrapperClassname,
        )}
        onClick={onComponentFocus}
        onBlur={onComponentBlur}
      >
        <Component
          {...(rest as T)}
          toggleHintArea={toggleHintArea}
          openHintArea={openHintArea}
          closeHintArea={closeHintArea}
          hintIcon={mobileHint && !!hintDescription}
          className={cn(
            rest.className,
            rest.disabled
              ? "sm:pointer-events-auto md:pointer-events-none"
              : undefined,
          )}
          onChange={onChange}
        />
        {canHintBeShown && (
          <HintArea
            className={"hidden transition-opacity max-md:flex"}
            hintAreaDetailsButtonText={hintAreaDetailsButtonText}
            hintAreaDetailsButton={hintAreaDetailsButton}
            onHintAreaDetailsButtonPressed={onHintAreaDetailsButtonPressed}
          >
            {hintDescription}
          </HintArea>
        )}
      </Stack>
    );
  };
  WithFieldHint.displayName = `WithFieldHint(${Component.displayName || Component.name})`;
  return WithFieldHint;
}
